import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './style/Header.module.scss';
import MenuButton from './components/MenuButton';
import HeaderLogo from './components/HeaderLogo';
import LinkButton from '../../components/Buttons/LinkButton';
import LoginButton from './components/LoginButton';
import moment from 'moment';
import { ReactComponent as Info } from '../../assets/icon/Info.svg';
import { ReactComponent as Marker } from '../../assets/icon/Marker.svg';
import { StoreContext } from '../../store/store';
import Notifications from './components/Notifications';
import ShoppingCartButton from './components/ShoppingCartButton';
import Api, { initApis } from '../../api/Api';
import { openInNewTab } from '../../generic/common';
import LoadingContext from '../../context/loadingContext/LoadingContext';
import { useUserProvider } from '../../generic/hooks';
import { DatePFormat } from '../../generic/StaticVariable';

type PrivateProps = {
  advertisingBannerClick: () => void;
};

export default function Header({ advertisingBannerClick }: PrivateProps) {
  const {
    user,
    showSubscriptionExpiredModal,
    setShowSubscriptionExpiredModal,
    setUser,
    setShoppingItems,
    setAddingPointToMap,
    addingPointToMap,
    showSearch,
    showPricingModal,
    showContactModal,
    showNotifications,
    setShowNotifications,
    setShowCart,
    showCart,
    setShowLogin,
    showLogin,
    advertisingBannerButtonText,
  } = useContext(StoreContext);
  const { isLoading } = useContext(LoadingContext);

  const history = useHistory();
  const [isAuthorized] = useUserProvider();

  useEffect(() => {
    tryToLoginSilently();
  }, []);

  const tryToLoginSilently = React.useCallback(async () => {
    try {
      await initApis();
      const user = await Api.currentUser.getCurrentUser();

      if (!user) {
        throw new Error('User retrieval failed');
      }

      setUser(user);
      const now = new Date();
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

      if (user?.subscriptionExpiryDate) {
        const subscriptionExpiryDateObj = new Date(user.subscriptionExpiryDate);
        const formattedSubscriptionDate = moment(subscriptionExpiryDateObj).format(DatePFormat);
        if (now > subscriptionExpiryDateObj) {
          setShowSubscriptionExpiredModal({
            header: 'Subscription expired',
            message: `Your subscription to ${user.subscriptionType} expired on ${formattedSubscriptionDate}.
                      Please contact customer care mike@dhigroup.com to renew your subscription`,
            status: true
          });
        } else if (subscriptionExpiryDateObj <= threeMonthsFromNow) {
          setShowSubscriptionExpiredModal({
            header: 'Subscription expiry',
            message: `Your subscription to ${user.subscriptionType} expires on ${formattedSubscriptionDate}.
                      Please contact customer care mike@dhigroup.com to extend your subscription`,
            status: true
          });
        }
      }
    } catch (error) {
      console.log('Error during login:', error);
      setUser({ authenticated: false });
    }
  }, []);

  useEffect(() => {

    const getShoppingCart = async () => {
      const data = await Api.shopping.GetShoppingCart();
      setShoppingItems(data.shoppingItems);
    }

    if (isAuthorized) {
      getShoppingCart();
    }
  }, [isAuthorized]);

  function onAddLocation() {
    setAddingPointToMap(!addingPointToMap);
  }

  const handleCart = () => {
    if (isAuthorized) {
      setShowCart(!showCart)
    } else {
      setShowLogin(!showLogin)
    }
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.left}>
          <HeaderLogo
            onClick={() => {
              history.push('/');
            }}
          />
          <MenuButton
            onClick={() => {
              openInNewTab(
                'https://www.dhigroup.com/data-portals/metocean-data-portal'
              );
            }}
            title={'About'}
            active={false}
          ></MenuButton>

          <MenuButton
            onClick={() => {
              history.push('pricing');
            }}
            title={'Pricing'}
            active={showPricingModal}
          ></MenuButton>

          <MenuButton
            onClick={() => {
              history.push('/metadata');
            }}
            title={'Datasets'}
            active={showSearch}
          ></MenuButton>

          <MenuButton
            onClick={() => {
              history.push('/features');
            }}
            title={'Features'}
            active={false}
          ></MenuButton>

          <MenuButton
            onClick={() => {
              history.push('/contact');
            }}
            title={'Contact'}
            active={showContactModal}
          ></MenuButton>
        </div>
        <div className={styles.right}>
          <div className={styles.linkButtonContainer}>
            <LinkButton
                onClick={() => openInNewTab('https://legacy.metocean-on-demand.com/')}
                title={<i>Go to Old Version</i>}>
              <></>
              </LinkButton>
          </div>
          <div className={styles.linkButtonContainer}>
            {localStorage.getItem('showAdvertisingBanner') === 'true' && (
              <LinkButton
                onClick={() => advertisingBannerClick()}
                title={advertisingBannerButtonText || 'Learn more'}
              >
                <Info className={styles.svg} />
              </LinkButton>
            )}
            <LinkButton
              onClick={() => {
                history.push('/getstarted');
              }}
              title="Get Started"
            >
              <Info className={styles.svg} />
            </LinkButton>
            <LinkButton
              onClick={onAddLocation}
              title="Make a new point"
              tooltip="Click here to add new point"
              buttonColor="blue"
              disable={isLoading}
            >
              <Marker className={styles.svg} />
            </LinkButton>
          </div>

          <div className={styles.iconsContainer}>
            <LinkButton title="Cart" smallHide={true} onClick={handleCart}>
              <ShoppingCartButton />
            </LinkButton>
            <LinkButton
              title="Notifications"
              smallHide={true}
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Notifications />
            </LinkButton>
          </div>

          <LoginButton />
        </div>
      </header>
    </>
  );
}
